import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import FemaleIcon from '@mui/icons-material/Female';
import InfoIcon from '@mui/icons-material/Info';
import MaleIcon from '@mui/icons-material/Male';
import PersonIcon from '@mui/icons-material/Person';
import React from 'react';

import {
  AiCallAgentDisplayMap,
  AiCallDetailDTO,
  AiVoice,
  completionStatusDisplay,
  SipTransferIntentDisplayMap,
  TimezoneAbbreviationEnum,
  toDisplayName
} from '_lib/api/apiTypes.shim';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemIcon } from '_lib/components/ListItemIcon/ListItemIcon';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { ShowMoreText } from '_lib/components/ShowMoreText/ShowMoreText';
import { Stack } from '_lib/components/Stack/Stack';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

import { formatPhone } from '_lib/utils/helpers';

import { HeaderBox } from '../HeaderBox/HeaderBox';
import { HeaderTextBlock } from '../HeaderTextBlock/HeaderTextBlock';
import { KeyValueGrid } from '../KeyValueGrid/KeyValueGrid';

import { AiCallTranscriptList } from './AiCallTranscriptList';
import { useUpdateAiCallDialog } from './hooks/UpdateAiCallDialogHook';

type AiCallDisplayProps = {
  aiCall: AiCallDetailDTO;
  aiVoice?: AiVoice;
  canEdit?: boolean;
  timezone: TimezoneAbbreviationEnum;
  gridItemSize: number;
};

export const AiCallDisplay = (props: AiCallDisplayProps) => {
  const { aiCall, aiVoice, canEdit, timezone, gridItemSize } = props;

  const updateAiCallDialog = useUpdateAiCallDialog();

  return (
    <FlexBox sx={{ width: '100%', height: '100%', flexDirection: 'column' }}>
      <Stack useFlexGap padding={2} spacing={2}>
        <KeyValueGrid
          gridItemSize={gridItemSize}
          keyValueArray={[
            { label: 'Caller Name', textOrComponent: aiCall.caller?.name || 'N/A' },
            {
              label: 'Caller Phone Number',
              textOrComponent: aiCall.caller?.phoneNumber ? formatPhone(aiCall.caller.phoneNumber) : 'N/A'
            },
            {
              label: 'Started At',
              // TODO: 'MMM D, YYYY h:mm:ss a'
              textOrComponent: DateUtils.format(aiCall.startedAt, DateFormatEnum.DateAtTime, {
                timezone,
                showTimezone: true
              })
            },
            {
              label: 'Ended At',
              // TODO: 'MMM D, YYYY h:mm:ss a'
              textOrComponent: DateUtils.format(aiCall.endedAt, DateFormatEnum.DateAtTime, {
                timezone,
                showTimezone: true
              })
            },
            { label: 'AI Agent', textOrComponent: (aiCall.agent && AiCallAgentDisplayMap[aiCall.agent]) || 'N/A' },
            {
              label: 'Outcome',
              textOrComponent: (
                <Stack spacing={0.5} direction='row' sx={{ alignItems: 'center' }}>
                  <Typography text={aiCall.outcome || 'N/A'} />
                  {!canEdit && (
                    <Tooltip placement='right' title='Outcome guessed from CPI call'>
                      <InfoIcon color='primary' fontSize='small' />
                    </Tooltip>
                  )}
                  {canEdit && (
                    <IconButton
                      size='small'
                      color='primary'
                      onClick={async () => {
                        await updateAiCallDialog.show({ aiCall });
                      }}>
                      <EditIcon />
                    </IconButton>
                  )}
                </Stack>
              )
            },
            {
              label: 'Completion Status',
              textOrComponent: (aiCall.completionStatus && completionStatusDisplay(aiCall.completionStatus)) || 'N/A'
            },
            {
              label: 'Business Transfer Outcome',
              textOrComponent:
                (aiCall.sipTransferIntent && SipTransferIntentDisplayMap[aiCall.sipTransferIntent]) || 'N/A'
            },
            { label: 'Ended Reason', textOrComponent: aiCall.providerCall?.endedReason?.split('-').join(' ') || 'N/A' },
            ...(aiCall.followUpReason ? [{ label: 'Follow Up Reason', textOrComponent: aiCall.followUpReason }] : []),
            ...(aiCall.companySwitched && aiCall.originalCompany
              ? [
                  {
                    label: 'Switched?',
                    textOrComponent: 'Yes'
                  },
                  {
                    label: 'Original Company',
                    textOrComponent: aiCall.originalCompany
                  }
                ]
              : []),
            {
              label: 'Voice',
              textOrComponent: !aiVoice ? (
                'No Voice Information Available'
              ) : (
                <Stack spacing={0.5} direction='row' sx={{ alignItems: 'center' }}>
                  {aiVoice.gender === 'male' ? (
                    <MaleIcon fontSize='small' sx={(theme) => ({ color: theme.palette.infoSecondary.main })} />
                  ) : (
                    <FemaleIcon fontSize='small' sx={(theme) => ({ color: theme.palette.infoSecondary.main })} />
                  )}
                  <Typography text={`${aiVoice.name} (${aiVoice.providerVoice.provider})`} />
                </Stack>
              )
            }
          ]}
        />
        <HeaderTextBlock headerText='Quick Summary:' text={aiCall.quickSummary || 'N/A'} />
        <HeaderBox headerText='Full Summary:'>
          <ShowMoreText text={aiCall.summary || 'N/A'} />
        </HeaderBox>
        {!!aiCall.providerData?.callerIdContext?.customers?.length && (
          <HeaderBox headerText='Caller ID Matching Customer(s):'>
            <WarningAlert>
              Customers below match the Caller ID number, but may not have been verified by the AI assistant.
            </WarningAlert>
            <List disablePadding>
              {aiCall.providerData.callerIdContext.customers.map((customer) => (
                <ListItem key={customer.providerId} dense>
                  <ListItemIcon sx={{ minWidth: '32px' }}>
                    {customer.isCommercial ? <BusinessIcon color='primary' /> : <PersonIcon color='primary' />}
                  </ListItemIcon>
                  <ListItemText primary={toDisplayName(customer)} />
                </ListItem>
              ))}
            </List>
          </HeaderBox>
        )}
        <HeaderBox headerText='Full Transcript:'>
          {!aiCall.transcriptMessages.length ? (
            <Typography text='No transcript found' />
          ) : (
            <AiCallTranscriptList
              transcriptMessages={aiCall.transcriptMessages}
              timezone={timezone}
              sx={(theme) => ({
                maxHeight: '450px',
                overflowY: 'auto',
                width: '100%',
                marginTop: 1,
                padding: 1.5,
                border: `2px solid ${theme.palette.divider}`,
                borderRadius: '4px',
                backgroundColor: theme.palette.background.default
              })}
              listItemProps={{ sx: (theme) => ({ backgroundColor: theme.palette.background.paper }) }}
            />
          )}
        </HeaderBox>
      </Stack>
      {updateAiCallDialog.element}
    </FlexBox>
  );
};
